import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { useDeleteAccountVelocityMembership } from '~/hooks/apis/users/useDeleteAccountVelocityMembership';
import { useGetAccountVelocityMemberships } from '~/hooks/apis/users/useGetAccountVelocityMemberships';
import type { VelocityMembership } from '~/services/UsersService';

import { getErrorMessage } from './utils';

type Props = {
  accountId: string;
};

export default function VelocityMembershipsTab({ accountId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [membershipPendingDelete, setMembershipPendingDelete] = useState<VelocityMembership | null>(null);
  const {
    data: memberships = [],
    isLoading,
    error,
  } = useGetAccountVelocityMemberships(accountId);
  const deleteMembershipMutation = useDeleteAccountVelocityMembership(accountId);

  const handleDelete = async () => {
    if (!membershipPendingDelete) {
      return;
    }

    try {
      await deleteMembershipMutation.mutateAsync(membershipPendingDelete.membership_id);
      setMembershipPendingDelete(null);
      enqueueSnackbar('Velocity membership removed', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to remove Velocity membership: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Velocity memberships
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Velocity memberships are saved when the customer successfully logs in to Velocity during checkout. Maximum of 2 accounts allowed by the system.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load Velocity memberships: {getErrorMessage(error)}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && !error && memberships.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No Velocity memberships have been linked to this account.
        </Typography>
      )}

      {memberships.length > 0 && (
        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
          {memberships.map((membership) => (
            <Stack
              key={membership.membership_id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              sx={{ py: 1 }}
            >
              <Typography>{membership.membership_id}</Typography>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => setMembershipPendingDelete(membership)}
                disabled={deleteMembershipMutation.isPending}
              >
                Remove
              </Button>
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog
        open={Boolean(membershipPendingDelete)}
        onClose={deleteMembershipMutation.isPending ? undefined : () => setMembershipPendingDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remove Velocity Membership</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Remove this Velocity membership from the account?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembershipPendingDelete(null)} disabled={deleteMembershipMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteMembershipMutation.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
